import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  ItBacklogItem, 
  CreateBacklogItemData, 
  UpdateBacklogItemData, 
  BacklogStats,
  BacklogStatus 
} from '@/types/itBacklog';
import { startOfWeek, endOfWeek, format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { useCurrentAppUser } from './useCurrentAppUser';

const QUERY_KEY = 'it-backlog';

// Fetch all backlog items with relations
export function useItBacklogItems(weekStart?: Date) {
  return useQuery({
    queryKey: [QUERY_KEY, weekStart?.toISOString()],
    queryFn: async () => {
      // Using raw query since it_backlog is not in generated types yet
      let query = supabase
        .from('it_backlog' as any)
        .select(`
          *,
          requester:requested_by(id, display_name, first_name, last_name),
          assignee:assigned_to(id, display_name, first_name, last_name),
          bug_report:related_bug_report_id(id, title)
        `)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      const { data, error } = await query;
      
      if (error) throw error;
      return data as unknown as ItBacklogItem[];
    },
  });
}

// Get stats for a specific week
export function useItBacklogStats(weekStart: Date) {
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  const weekStartStr = format(weekStart, 'yyyy-MM-dd');
  const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
  
  return useQuery({
    queryKey: [QUERY_KEY, 'stats', weekStartStr],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('it_backlog' as any)
        .select('*');
      
      if (error) throw error;
      
      const items = data as unknown as ItBacklogItem[];
      
      const thisWeekItems = items.filter(item => 
        item.planned_week && 
        item.planned_week >= weekStartStr && 
        item.planned_week <= weekEndStr
      );
      
      const stats: BacklogStats = {
        total: items.filter(i => i.status !== 'done').length,
        thisWeek: thisWeekItems.filter(i => i.status !== 'done').length,
        critical: items.filter(i => 
          (i.priority === 'critical' || i.priority === 'urgent') && 
          i.status !== 'done'
        ).length,
        plannedHours: thisWeekItems
          .filter(i => i.status !== 'done')
          .reduce((sum, i) => sum + (i.estimated_hours || 0), 0),
        completedThisWeek: thisWeekItems.filter(i => i.status === 'done').length,
      };
      
      return stats;
    },
  });
}

// Create a new backlog item
export function useCreateBacklogItem() {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentAppUser();
  
  return useMutation({
    mutationFn: async (data: CreateBacklogItemData) => {
      const insertData = {
        ...data,
        requested_by: data.requested_by || currentUser?.id,
        status: 'backlog',
      };
      
      const { data: result, error } = await supabase
        .from('it_backlog' as any)
        .insert(insertData)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Tarefa criada com sucesso!');
    },
    onError: (error) => {
      console.error('Error creating backlog item:', error);
      toast.error('Erro ao criar tarefa');
    },
  });
}

// Update a backlog item
export function useUpdateBacklogItem() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateBacklogItemData }) => {
      const { data: result, error } = await supabase
        .from('it_backlog' as any)
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (error) => {
      console.error('Error updating backlog item:', error);
      toast.error('Erro ao atualizar tarefa');
    },
  });
}

// Update status (for drag & drop)
export function useUpdateBacklogStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      id, 
      status, 
      plannedWeek 
    }: { 
      id: string; 
      status: BacklogStatus; 
      plannedWeek?: string | null;
    }) => {
      const updateData: any = { status };
      
      // Set planned_week when moving to planned
      if (status === 'planned' && plannedWeek) {
        updateData.planned_week = plannedWeek;
      }
      
      // Set started_at when moving to in_progress
      if (status === 'in_progress') {
        updateData.started_at = new Date().toISOString();
      }
      
      // Set completed_at when moving to done
      if (status === 'done') {
        updateData.completed_at = new Date().toISOString();
      }
      
      const { data: result, error } = await supabase
        .from('it_backlog' as any)
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (error) => {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    },
  });
}

// Delete a backlog item
export function useDeleteBacklogItem() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('it_backlog' as any)
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Tarefa removida!');
    },
    onError: (error) => {
      console.error('Error deleting backlog item:', error);
      toast.error('Erro ao remover tarefa');
    },
  });
}

// Fetch feature requests/improvements for linking (NOT bugs)
export function useBugReports() {
  return useQuery({
    queryKey: ['feature-requests-for-backlog'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bug_reports')
        .select('id, title, status, priority, type')
        .in('type', ['feature', 'improvement'])
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data;
    },
  });
}

// Fetch admin users for assignment
export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users-for-backlog'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_users')
        .select(`
          id, 
          display_name, 
          first_name, 
          last_name,
          system_role:system_roles(name, code)
        `)
        .eq('active', true);
      
      if (error) throw error;
      
      // Filter admins
      return data?.filter(u => 
        u.system_role?.code === 'admin' || 
        u.system_role?.name === 'Admin'
      ) || [];
    },
  });
}
